import { getRouteApi } from '@tanstack/react-router';
import { PaginationState, SortingState } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { useMediaQuery } from 'usehooks-ts';

import { getDocumentsDefaultParams } from '@/features/case-list/actions/getDocuments';
import { Document } from '@/features/case-list/api/types';
import { DocumentsTable } from '@/features/case-list/components/DocumentsTable';
import { useGetDocuments } from '@/features/case-list/queries/getDocuments';

const DOCUMENTS_PAGE_SIZE = parseInt(getDocumentsDefaultParams.limit);

const emptyDocuments: Document[] = [];

const route = getRouteApi('/admin/case-standards/');

export function DocumentsListRoot() {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const isLargeScreen = useMediaQuery('(min-height: 1000px)');

  const tableHeight = isLargeScreen ? 800 : 600;

  const pagination = useMemo(
    () =>
      ({
        pageIndex: search.page,
        pageSize: search.perPage,
      }) as PaginationState,
    [search.page, search.perPage]
  );

  const setPagination = useCallback(
    (pagination: PaginationState) => {
      navigate({
        search: {
          ...search,
          page: pagination.pageIndex,
          perPage: pagination.pageSize,
        },
      });
    },
    [navigate, search]
  );

  const sorting = useMemo(
    () => [
      {
        id: search.sort,
        desc: search.orderBy === 'desc',
      },
    ],
    [search.sort, search.orderBy]
  );

  const setSorting = useCallback(
    (sorting: SortingState) => {
      navigate({
        search: {
          ...search,
          sort: sorting[0]?.id,
          orderBy: sorting[0]?.desc ? 'desc' : 'asc',
        },
      });
    },
    [navigate, search]
  );

  const {
    data,
    isLoading,
    error,
    isFetching: isFetchingDocuments,
  } = useGetDocuments({
    sort: sorting[0]?.id,
    orderBy: sorting[0]?.desc ? 'desc' : 'asc',
    limit: String(pagination.pageSize),
    offset: String(pagination.pageIndex * pagination.pageSize),
    search: search.search,
  });

  const documents = useMemo(() => data?.CFDocuments || emptyDocuments, [data]);
  const totalCount = data?.totalCount || 0;

  if (isLoading) {
    return <div className="p-4">Loading documents...</div>;
  }

  if (error) {
    return <div className="p-4 text-red-600">{error.message}</div>;
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Documents</h1>
        <div className="text-sm text-muted-foreground">
          {totalCount} document{totalCount === 1 ? '' : 's'} total
        </div>
      </div>

      <div className="px-4 py-2 flex flex-col gap-2">
        <DocumentsTable
          documents={documents}
          totalCount={totalCount}
          pageSize={DOCUMENTS_PAGE_SIZE}
          onPaginationChange={setPagination}
          pagination={pagination}
          sorting={sorting}
          onSortingChange={setSorting}
          pageCount={data?.pageCount ?? 0}
          isFetching={isFetchingDocuments}
          height={tableHeight}
        />
      </div>
    </div>
  );
}
